import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  useCortexModelsList,
  useCreateAIModel,
} from "@via/pages/cortex/model-garden/model-garden.api.tsx";
import {
  useCreateProcessMenuGroup,
  useProcessMenuGroupsList,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { useIntegrationTokens } from "@via/api/integration-tokens.api";
import { SelectOption } from "@via/utils/interfaces.tsx";
import {
  AIModelType,
  CortexRegistryType,
  CreateAIModelRequestBody,
  CreateAIModelRequestBodyBase,
  CreateAIModelRequestBodyEndpoint,
  CreateAIModelRequestBodyTamarind,
  CreateAIModelRequestBodyVertexAI,
  MenuGroupType,
  VertexAIModelSourceType,
} from "common";
import { useEffect, useMemo, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { Options } from "react-select";
import { useDebounceCallback } from "usehooks-ts";

interface CreateAIModelFormProps {
  setOpen: (open: boolean) => void;
}

type ModelOption = {
  label: string;
  value: string;
  version: string;
  modelName: string;
  publisher: string;
  description: string;
  displayName?: string;
  pathName?: string;
};

// Extended form values to include both endpoint and model fields
interface CreateAIModelFormValues {
  name: string;
  menuGroupId: number;
  type: AIModelType;
  registry: CortexRegistryType;
  // For Model type
  modelSource?: VertexAIModelSourceType;
  /**
   * name of the model in the model garden
   * example: text-bison
   */
  modelName: string;
  version?: string;
  publisher?: string;
  /**
   * name of the model in the model registry
   * example: publishers/google/models/text-bison
   */
  pathName?: string;
  // For Endpoint type
  endpointUrl?: string;
  endpointId?: string;
  // For Tamarind registry
  serviceTokenId?: number;
}

const CreateAIModelForm: FC<CreateAIModelFormProps> = ({ setOpen }) => {
  const {
    mutate: createAIModel,
    isPending: isCreateAIModelLoading,
    isSuccess: isCreateAIModelSuccess,
  } = useCreateAIModel();
  const { user } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateAIModelFormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
      menuGroupId: undefined,
      type: AIModelType.Model,
      registry: CortexRegistryType.VertexAI,
      modelSource: VertexAIModelSourceType.ModelGarden,
      publisher: "",
      modelName: "",
      version: "",
      endpointUrl: "",
      endpointId: "",
      pathName: "",
      serviceTokenId: undefined,
    },
  });

  // Watch type to conditionally render fields
  const selectedType = watch("type");
  const isEndpointType = selectedType === AIModelType.Endpoint;
  const selectedModelSource = watch("modelSource");
  const selectedRegistry = watch("registry");
  const isTamarind = selectedRegistry === CortexRegistryType.Tamarind;

  // Fetch Tamarind tokens when Tamarind is selected
  const { data: tamarindTokensData, isLoading: isTamarindTokensLoading } =
    useIntegrationTokens(
      0,
      100,
      [],
      [{ id: "tokenTypeIn", value: ["tamarind"] }],
      false
    );

  const tamarindTokenOptions = useMemo(
    () =>
      tamarindTokensData?.data?.map(token => ({
        label: token.name,
        value: token.id,
      })) || [],
    [tamarindTokensData?.data]
  );

  // Fetch menu groups for AI models
  const { data: menuGroupsData, isLoading: isMenuGroupsLoading } =
    useProcessMenuGroupsList(MenuGroupType.AI_MODEL.toString(), true);

  const {
    mutate: createNewMenuGroup,
    data: newMenuGroupData,
    isSuccess: isCreateNewMenuGroupSuccess,
  } = useCreateProcessMenuGroup();

  // State for model search
  const [modelSearchInput, setModelSearchInput] = useState("");

  // Debounce the search input
  const debouncedHandleModelInputChange = useDebounceCallback(
    (value: string) => {
      setModelSearchInput(value);
    },
    300
  );

  // Fetch Vertex AI models from Model Garden (only when Model type is selected)
  const { data: cortexModelsData, isLoading: isCortexModelsLoading } =
    useCortexModelsList(
      modelSearchInput || undefined,
      selectedRegistry,
      selectedModelSource,
      !isEndpointType
    );

  const menuGroupOptions = useMemo(
    () =>
      menuGroupsData?.data?.map(group => ({
        label: group.name,
        value: group.id,
      })) || [],
    [menuGroupsData?.data]
  );
  const modelOptions = useMemo(
    () =>
      cortexModelsData?.map(model => {
        return {
          label: `[${model.publisher}] ${model.displayName} ${model.version ? `(${model.version})` : ""}`,
          value: model.name,
          version: model.version,
          modelName: model.name,
          publisher: model.publisher,
          description: model.description,
          displayName: model.displayName,
          pathName: model.pathName,
        } as ModelOption;
      }) || [],
    [cortexModelsData]
  );

  useEffect(() => {
    if (isCreateAIModelSuccess) {
      setOpen(false);
    }
  }, [isCreateAIModelSuccess, setOpen]);

  // Handle menu group creation success
  useEffect(() => {
    if (isCreateNewMenuGroupSuccess && newMenuGroupData) {
      // Update the form with the newly created menu group
      setValue("menuGroupId", newMenuGroupData.id);
    }
  }, [isCreateNewMenuGroupSuccess, newMenuGroupData, setValue]);

  const handleCreateAIModel = (payload: CreateAIModelFormValues): void => {
    const baseBody: CreateAIModelRequestBodyBase = {
      name: payload.name,
      menuGroupId: payload.menuGroupId,
      registry: payload.registry,
      type: payload.type,
      modelName: payload.modelName,
      publisher: payload.publisher || "",
      version: payload.version?.trim() || "",
    };
    let body: CreateAIModelRequestBody;
    if (baseBody.type === AIModelType.Endpoint) {
      // Endpoint type payload
      body = Object.assign(baseBody, {
        endpoint: {
          dedicatedDns: payload.endpointUrl,
          id: payload.endpointId,
        },
      } as CreateAIModelRequestBodyEndpoint);
    } else if (baseBody.registry === CortexRegistryType.Tamarind) {
      baseBody.publisher = "tamarind";
      body = Object.assign(baseBody, {
        serviceTokenId: payload.serviceTokenId,
      } as CreateAIModelRequestBodyTamarind);
    } else {
      body = Object.assign(baseBody, {
        isPrivate:
          payload.modelSource === VertexAIModelSourceType.ModelRegistry,
        pathName: payload.pathName,
      } as CreateAIModelRequestBodyVertexAI);
    }
    createAIModel(body);
  };

  const typeOptions = [
    { label: "Model", value: AIModelType.Model },
    { label: "Endpoint", value: AIModelType.Endpoint },
  ];

  const registryOptions = [
    { label: "Vertex AI", value: CortexRegistryType.VertexAI },
    { label: "Tamarind", value: CortexRegistryType.Tamarind },
    user?.featureFlags?.FF_DISPLAY_DEMO_DATA
      ? { label: "SageMaker", value: CortexRegistryType.SageMaker }
      : undefined,
  ].filter(option => option !== undefined) as SelectOption[];

  const modelSourceOptions = [
    {
      label: "Model Garden (Published models)",
      value: VertexAIModelSourceType.ModelGarden,
    },
    {
      label: "Model Registry (Private models)",
      value: VertexAIModelSourceType.ModelRegistry,
    },
  ];

  const handleCreateMenuGroup = (name: string) => {
    const payload = {
      name,
      type: MenuGroupType.AI_MODEL,
    };
    createNewMenuGroup(payload);
  };

  // Handle model selection to also set version
  const handleModelChange = (option: SelectOption | Options<SelectOption>) => {
    if (!option || Array.isArray(option)) {
      return;
    }
    const typedOption = option as ModelOption;
    setValue("version", typedOption.version || "");
    setValue("modelName", typedOption.modelName || "");
    setValue("publisher", typedOption.publisher || "");
    setValue("pathName", typedOption.pathName || "");
  };

  const onSubmit = handleSubmit(handleCreateAIModel);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Register AI Model
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => setOpen(false)}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to register your
              new AI Model.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Platform <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="registry"
                    rules={{ required: "Platform is required" }}
                    options={registryOptions}
                    enableClear={false}
                  />
                  {errors.registry && (
                    <span className="text-xs text-rose-500">
                      {errors.registry.message as string}
                    </span>
                  )}
                </div>
              </div>

              {/* Tamarind Token Selection */}
              {isTamarind && (
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Tamarind Token <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <SelectComponent
                      control={control}
                      name="serviceTokenId"
                      rules={{ required: "Tamarind token is required" }}
                      placeholder="Select a Tamarind token"
                      options={tamarindTokenOptions}
                      enableClear={false}
                      isLoading={isTamarindTokensLoading}
                    />
                    {errors.serviceTokenId && (
                      <span className="text-xs text-rose-500">
                        {errors.serviceTokenId.message as string}
                      </span>
                    )}
                    {!isTamarindTokensLoading &&
                      tamarindTokenOptions.length === 0 && (
                        <p className="mt-1 text-xs text-amber-600">
                          No Tamarind tokens found. Please create one in your
                          profile settings.
                        </p>
                      )}
                  </div>
                </div>
              )}

              {/* Type */}
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Type <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="type"
                    rules={{ required: "Type is required" }}
                    options={typeOptions}
                    enableClear={false}
                  />
                  {errors.type && (
                    <span className="text-xs text-rose-500">
                      {errors.type.message as string}
                    </span>
                  )}
                </div>
              </div>

              {!isEndpointType && !isTamarind && (
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Model Source <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <SelectComponent
                      control={control}
                      name="modelSource"
                      rules={{ required: "Model source is required" }}
                      options={modelSourceOptions}
                      enableClear={false}
                    />
                    {errors.modelSource && (
                      <span className="text-xs text-rose-500">
                        {errors.modelSource.message as string}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Conditional: Model selector OR Endpoint input */}
              {isEndpointType ? (
                <FormInputComponent
                  control={control}
                  name="endpointUrl"
                  label="Endpoint URL"
                  placeholder="Enter your endpoint URL"
                  showRequiredField={true}
                  validations={{
                    required: "Endpoint URL is required",
                    minLength: {
                      value: 1,
                      message: "Endpoint URL must be at least 1 character",
                    },
                  }}
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Model <span className="text-red-500">*</span>
                  </label>
                  {selectedRegistry === CortexRegistryType.VertexAI &&
                    (selectedModelSource ===
                    VertexAIModelSourceType.ModelGarden ? (
                      <p className="mt-1 text-sm text-gray-500">
                        Browse and select a model from the model garden. You can
                        search by name or publisher. Format: [publisher]
                        modelName (version)
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-gray-500">
                        Browse and select a model from the model registry. You
                        can search by name or publisher. Format: [projectId]
                        modelName (version)
                      </p>
                    ))}
                  <div className="mt-2">
                    <SelectComponent
                      control={control}
                      name="modelName"
                      rules={{ required: "Model is required" }}
                      placeholder="Select a model from model garden"
                      options={modelOptions}
                      enableClear={false}
                      isLoading={isCortexModelsLoading}
                      onInputChange={debouncedHandleModelInputChange}
                      onChange={handleModelChange}
                      maxMenuHeight={500}
                      formatOptionLabel={(option, { context }) => {
                        if (context === "value") {
                          return option.label;
                        }
                        return (
                          <div>
                            <div>{option.label}</div>
                            {option.description && (
                              <div className="text-xs text-gray-500 mt-1">
                                {option.description}
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                    {errors.modelName && (
                      <span className="text-xs text-rose-500">
                        {errors.modelName.message as string}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {isEndpointType && (
                <FormInputComponent
                  control={control}
                  name="endpointId"
                  label="Endpoint ID"
                  placeholder="Enter your endpoint ID"
                  showRequiredField={true}
                  validations={{
                    required: "Endpoint ID is required",
                    minLength: {
                      value: 1,
                      message: "Endpoint ID must be at least 1 character",
                    },
                  }}
                />
              )}

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Menu Group <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="menuGroupId"
                    rules={{ required: "Menu group is required" }}
                    placeholder="Select or create a menu group"
                    options={menuGroupOptions}
                    enableClear={false}
                    isLoading={isMenuGroupsLoading}
                    isCreatable={true}
                    onCreateOption={handleCreateMenuGroup}
                  />
                  {errors.menuGroupId && (
                    <span className="text-xs text-rose-500">
                      {errors.menuGroupId.message as string}
                    </span>
                  )}
                </div>
              </div>

              {/* Display Name */}
              <FormInputComponent
                control={control}
                name="name"
                label="Display Name"
                placeholder="Name your AI Model"
                showRequiredField={true}
                validations={{
                  required: "Display name is required",
                  minLength: {
                    value: 1,
                    message: "Display name must be at least 1 character",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={isCreateAIModelLoading}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
          <Spinner loading={isCreateAIModelLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
};

export default CreateAIModelForm;
